/**
 * One group's rows on the Live card (slice 3): Today's list only once a
 * verdict judged it (its badge — reason + what the verdict DID — scaling,
 * test day; the DEV-only Simulate control rides there too), every burned or
 * skipped list struck with its reason and age, and — when the pool is
 * exhausted — a paused band; Reset pool lives in ./controls.
 * 23/10/2026 (client QA: "Remove/Hide this"): a Today row with nothing
 * judged and the Next row ("if today fails") are gone — the Day timeline's
 * List column names each drop's list.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import type { DayPlanGroupRow, DropGenGroupVerdict } from '@trafficpulse/shared';

import { Badge, Tooltip } from '@/components/ui';

import type { DayPlanContainerRow } from '../../drop-generator.types';
import { GroupChip } from '../shared/GroupChip';
import { ResetPoolControl } from './controls/ResetPoolControl';
import { SimulateControl, type SimulateRequest } from './controls/SimulateControl';
import { groupCounts, groupUnsentDrafts } from './live-status';
import { TodayListRows } from './lists/TodayListRows';
import { actionCopy, scalingCopy, VERDICT_TONE } from './lists/verdict-copy';


const ROW = 'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm';
/** A 26-list group burned whole must not become a wall — the mock's spill. */
const MAX_STRUCK_ROWS = 5;
const LABEL = 'w-11 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';


/** Phase 31 — a struck list's return day, DD/MM. */
const returnsLabel = (iso: string) => `returns ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;


const ageOf = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'today' : `d−${days}`;
};

export const GroupLiveRows: FC<{
  container: DayPlanContainerRow;
  group: DayPlanGroupRow;
  verdict?: DropGenGroupVerdict;
  /** 19/10/2026 — each of today's lists judged on its own (several lists a day). */
  listVerdicts?: DropGenGroupVerdict[];
  onResetPool?: () => void;
  resetting?: boolean;
  /** DEV ONLY — present only when the backend simulation is on. */
  onSimulate?: (req: SimulateRequest) => void;
  simulating?: boolean;
  /** The day has runtime rows — a simulation has something to fake. */
  generated?: boolean;
}> = ({ container, group, verdict: groupVerdict, listVerdicts, onResetPool, resetting = false, onSimulate, simulating = false, generated = false }) => {
  const counts = groupCounts(container, group.listGroupId);
  const several = (group.listsToday?.length ?? 0) > 1;
  // A group judged list by list (listsPerDay ≠ 1) that sends one list today shows that list's own verdict.
  const ownVerdict = groupVerdict?.perList ? listVerdicts?.find((v) => v.listWiserId === group.listOfDay?.wiserId) : groupVerdict;
  const verdict = several ? undefined : ownVerdict;
  const multi = container.groups.length > 1;
  const burned = group.skipped.filter((s) => s.burnedAt).length;
  const action = verdict ? actionCopy(verdict) : '';
  const listJudged = several && (listVerdicts?.length ?? 0) > 0;
  const showToday = Boolean(verdict || group.returning || listJudged || (onSimulate && generated));
  if (!group.paused && !showToday && group.skipped.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {group.paused ? (
        <div className={`${ROW} border border-warning/40 bg-warning/10`}>
          {multi ? <GroupChip name={group.groupName} color={group.groupColor} /> : null}
          <span className="text-xs font-medium text-warning">paused — pool exhausted</span>
          <span className="min-w-0 grow truncate text-xs text-muted-foreground">
            {group.paused.burned} list{group.paused.burned === 1 ? '' : 's'} burned
          </span>
        </div>
      ) : showToday ? (
        <div className={`${ROW} border border-primary/30 bg-primary/5`}>
          <span className={LABEL}>Today</span>
          {multi ? <GroupChip name={group.groupName} color={group.groupColor} /> : null}
          <span className="min-w-0 grow truncate font-mono text-xs">
            {several ? `${group.listsToday?.length} lists` : (group.listOfDay?.name ?? '—')}
          </span>
          {verdict ? (
            <Tooltip content={action ? `${verdict.reason} · ${action}` : verdict.reason}>
              <Badge tone={VERDICT_TONE[verdict.verdict] ?? 'secondary'}>
                {verdict.verdict.toLowerCase()}
              </Badge>
            </Tooltip>
          ) : null}
          {verdict?.scaling && verdict.scaling.steps !== 0 ? (
            <Tooltip content={scalingCopy(verdict.scaling)}>
              <Badge tone={verdict.scaling.steps > 0 ? 'success' : 'warning'}>×{verdict.scaling.factor}</Badge>
            </Tooltip>
          ) : null}
          {group.returning ? (
            <Tooltip content={`Back from a burn after ${group.returning.restDays ?? 'its'} day${group.returning.restDays === 1 ? '' : 's'} of rest — ${group.returning.testAmount ? `${group.returning.testAmount.toLocaleString('en-US')} SMS per drop today to try it` : 'the usual amount today'}; a FAIL today burns it again.`}>
              <Badge tone="warning">test day</Badge>
            </Tooltip>
          ) : null}
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
            {counts.sent} sent · {counts.planned - counts.sent} planned
          </span>
          {onSimulate && generated ? (
            <SimulateControl
              groupName={group.groupName}
              unsent={groupUnsentDrafts(container, group.listGroupId)}
              onSimulate={onSimulate}
              busy={simulating}
            />
          ) : null}
        </div>
      ) : null}
      {listJudged && !group.paused ? <TodayListRows container={container} group={group} verdicts={listVerdicts} /> : null}
      {group.skipped.slice(0, MAX_STRUCK_ROWS).map((skip) => (
        <div key={skip.wiserId} className={`${ROW} py-1 text-xs text-muted-foreground opacity-80`}>
          <span className={LABEL} />
          <span className="min-w-0 grow truncate font-mono line-through">
            {skip.name ?? `#${skip.wiserId}`}
          </span>
          <Badge tone={skip.reason === 'terrible' ? 'destructive' : 'warning'}>{skip.reason}</Badge>
          <span className="shrink-0 font-mono text-[11px] tabular-nums">
            {skip.burnedAt ? ageOf(skip.burnedAt) : 'today'}
            {skip.returnsOn ? ` · ${returnsLabel(skip.returnsOn)}` : ''}
          </span>
        </div>
      ))}
      {group.skipped.length > MAX_STRUCK_ROWS ? (
        <div className="pl-[52px] text-[11px] text-muted-foreground">
          + {group.skipped.length - MAX_STRUCK_ROWS} more burned list
          {group.skipped.length - MAX_STRUCK_ROWS === 1 ? '' : 's'}
        </div>
      ) : null}
      {burned > 0 && onResetPool ? (
        <ResetPoolControl
          groupName={group.groupName}
          containerName={container.name}
          burned={burned}
          onReset={onResetPool}
          resetting={resetting}
        />
      ) : null}
    </div>
  );
};
